// Pagination — the footer under a DataTable.
//
// Shows the range ("1–25 of 340") rather than only page numbers, because the
// first question staff ask a table is how much is in it. Page-size choice is
// offered inline; both land in the URL via useTableState.

import { cn } from '@fajrarabi/ui';
import { useId } from 'react';

import { Icon } from './Icon';

export interface PaginationProps {
  page: number;
  pageSize: number;
  /** Total row count across all pages. */
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: readonly number[];
  className?: string;
}

export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [25, 50, 100],
  className,
}: PaginationProps) {
  const sizeId = useId();
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const clampedPage = Math.min(Math.max(1, page), pageCount);
  const first = total === 0 ? 0 : (clampedPage - 1) * pageSize + 1;
  const last = Math.min(clampedPage * pageSize, total);

  // A single page of results needs no controls at all, but the count is still
  // worth showing — it answers "is this everything?".
  const showControls = pageCount > 1;

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 text-sm text-admin-600',
        className,
      )}
    >
      <p aria-live="polite">
        {total === 0 ? (
          'No results'
        ) : (
          <>
            <span className="font-medium text-admin-800 tabular-nums">
              {first.toLocaleString('en-US')}–{last.toLocaleString('en-US')}
            </span>{' '}
            of{' '}
            <span className="font-medium text-admin-800 tabular-nums">
              {total.toLocaleString('en-US')}
            </span>
          </>
        )}
      </p>

      <div className="flex items-center gap-3">
        {onPageSizeChange && total > (pageSizeOptions[0] ?? 25) && (
          <div className="flex items-center gap-1.5">
            <label htmlFor={sizeId} className="text-xs text-admin-500">
              Per page
            </label>
            <select
              id={sizeId}
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="h-8 rounded-lg border border-admin-200 bg-white px-2 text-sm text-admin-700 focus:border-brand-500 focus:outline-none"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        )}

        {showControls && (
          <nav aria-label="Pagination" className="flex items-center gap-1">
            <PageButton
              onClick={() => onPageChange(clampedPage - 1)}
              disabled={clampedPage <= 1}
              label="Previous page"
              icon="chevronStart"
            />
            <span className="px-2 text-sm tabular-nums text-admin-600">
              {clampedPage} / {pageCount}
            </span>
            <PageButton
              onClick={() => onPageChange(clampedPage + 1)}
              disabled={clampedPage >= pageCount}
              label="Next page"
              icon="chevronEnd"
            />
          </nav>
        )}
      </div>
    </div>
  );
}

function PageButton({
  onClick,
  disabled,
  label,
  icon,
}: {
  onClick: () => void;
  disabled: boolean;
  label: string;
  icon: 'chevronStart' | 'chevronEnd';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="grid h-8 w-8 place-items-center rounded-lg border border-admin-200 bg-white text-admin-600 transition-colors hover:bg-admin-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white"
    >
      {/* Prev/next are reading-direction concepts, so they mirror in RTL. */}
      <Icon name={icon} className="h-4 w-4 rtl:-scale-x-100" />
    </button>
  );
}
